import { AsyncPipe, NgClass } from '@angular/common'
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete'
import { MatOptionModule } from '@angular/material/core'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { InfiniteScrollContainerComponent } from '@wm-front/shared/infinite-scroll'
import { Subject } from 'rxjs'
import { debounceTime, distinctUntilChanged } from 'rxjs/operators'
import { DropdownOption } from '@wm-front/core'
// eslint-disable-next-line @nx/enforce-module-boundaries
import { latinise } from '@wm-front/shared/utils'

declare const $localize: any

@UntilDestroy()
@Component({
  selector: 'wm-front-autocomplete-dropdown-select',
  templateUrl: './autocomplete-dropdown-select.component.html',
  styleUrls: ['./autocomplete-dropdown-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgClass,
    FormsModule,
    MatAutocompleteModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatOptionModule,
    InfiniteScrollContainerComponent,
    MatProgressSpinnerModule,
    AsyncPipe,
  ],
})
export class AutocompleteDropdownSelectComponent implements OnInit {
  /** The input's label */
  @Input() public label?: string

  /** Draws the required mark after the label (PRENG-8287), instead of a star written in the label's translation */
  @Input() public requiredMark = false

  /** The placeholder to display in the autocomplete input */
  @Input() public placeholder: string = ''

  /** The debounce time of the search query */
  @Input() public debounceTime = 50

  /** Whether the query is loading */
  @Input() public loading = false

  /** Whether there is an error */
  @Input() public error: Error | null

  /** Whether there the dropdown should be disabled */
  @Input() public disabled = false

  /** Do you want a clear up icon to remove the selection and cancel the query, this is the place you should tell me */
  @Input() public fastDelete = false

  /** Label shown when no result */
  @Input()
  public noResultLabel: string = $localize`:Label | No result label@@autoCompleteDropdownSelectDefaultNoResultLabel:Aucun résultat`

  /** Reference to input element */
  @ViewChild('input') public input: ElementRef

  /** Event that triggers when a value is picked in the dropdown */
  @Output() public optionSelected: EventEmitter<
    MatAutocompleteSelectedEvent | undefined
  > = new EventEmitter()

  /** Event that triggers when the search value in changed */
  @Output() public queryChange: EventEmitter<string> = new EventEmitter()

  /** Event that triggers when the options checked needs to be resetted */
  @Output() public scrolled: EventEmitter<void> = new EventEmitter()

  /** The query type in the input field */
  public query: string

  /** The subject holding the changes of the query */
  public queryChanged: Subject<string> = new Subject<string>()

  /** The filtered array of options */
  public filteredOptions: DropdownOption[]

  /** The array of data to filter */
  private _options: DropdownOption[] = []

  public get options(): DropdownOption[] {
    return this._options
  }

  @Input()
  public set options(opts: DropdownOption[]) {
    this._options = opts || []
    /** opens the options panel on options filling */
    this.filteredOptions = this.filter('')
  }

  /** Will select the provided option */
  @Input()
  public set optionToSelect(option: DropdownOption | undefined) {
    this.query = option?.label || ''
  }

  public ngOnInit(): void {
    this.listenToQueryChanges()
  }

  /** Used to pick a value from the dropdown
   * @param { MatAutocompleteSelectedEvent } option the option picked from the checkbox
   */
  public pickValue(event: MatAutocompleteSelectedEvent): void {
    this.optionSelected.emit(event)
  }

  /** Should filter the array of options and re assign them to the filtered options variable
   * @param { string } value the value we receive to filter
   */
  public filterOptions(value: string): void {
    if (typeof value === 'string') {
      this.queryChanged.next(value)
      this.filteredOptions = this.filter(value)

      /** When the user deletes all that the input holds, he removes his selection */
      if (!value) this.optionSelected.emit(undefined)
    }
  }

  /** Used to filter the array of options with the term we receive from the input
   * @param { string } value the value we receive as string
   * @return { Array<DropdownOption> }
   */
  public filter(value: string): DropdownOption[] {
    let filterValue = ''
    if (value) {
      filterValue = latinise(value.toLowerCase())
    }

    return (this.options || []).filter((option: DropdownOption): boolean =>
      latinise(option.label?.toLowerCase()).includes(filterValue)
    )
  }

  /**
   * Mapper function fed to mat's autocomplete component
   * @param option
   */
  public displayOptionValue(option?: DropdownOption | string): string {
    if (option && typeof option === 'string') {
      return option
    }
    return (option as DropdownOption)?.label || ''
  }

  /**
   * Resets the selected option and the current query before emitting this non selection
   * Used by the clearInput icon
   */
  public reset(): void {
    this.query = ''
    this.optionSelected.emit(undefined)
    this.input.nativeElement.focus()
  }

  /** Trackby method to prevent recreation of items
   * @param { number }
   * @param { DropdownOption }
   * @returns { string }
   */
  public trackByFn(i: number, item: DropdownOption): string {
    return item.value as string
  }

  /** Listens to the query changes and emit the value changed to the parent component */
  private listenToQueryChanges(): void {
    this.queryChanged
      .pipe(
        debounceTime(this.debounceTime),
        distinctUntilChanged(),
        untilDestroyed(this)
      )
      .subscribe((model) => {
        this.query = model
        this.queryChange.emit(this.query)
      })
  }
}
